import React, { useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { Alert, AlertTitle, Breadcrumbs, Button, Chip, Link, Stack, Typography } from "@mui/material";
import useAsyncRequest from "hooks/useAsyncRequest";
import { LoadablePage } from "shared/LoadablePage";
import { OrderStatus } from "client/api-v2";
import type { OrderDto, OrderFulfillmentDto } from "client/api-v2";
import { ActionDialog } from "components/ActionDialog/ActionDialog";
import { AlertSeverity, useAlerts } from "components/AlertProvider/AlertProvider";
import { DetailsPageLayout } from "components/DetailsPageLayout/DetailsPageLayout";
import { PageContent, pageContentMaxWidth } from "components/PageContent/PageContent";
import { PageHeaderBar } from "components/PageHeaderBar/PageHeaderBar";
import { UnsavedChangesDialog } from "components/UnsavedChangesDialog/UnsavedChangesDialog";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { useRbacContext } from "components/authorization/RbacProvider";
import { BuilderLayout } from "areas/orders/builder/BuilderLayout";
import { BuilderSection } from "areas/orders/builder/BuilderSection";
import { isOrderClosedToEdits, LockedOrderBanner } from "areas/orders/builder/LockedOrderBanner";
import { OrderSummaryRail } from "areas/orders/builder/OrderSummaryRail";
import { BillingAccountsSection } from "areas/orders/builder/sections/BillingAccountsSection";
import {
  CommercialTermsSection,
  validateSpecialLicenceTerms,
} from "areas/orders/builder/sections/CommercialTermsSection";
import { InternalReviewSection } from "areas/orders/builder/sections/InternalReviewSection";
import { NotesSection } from "areas/orders/builder/sections/NotesSection";
import { OrderProcessingSection } from "areas/orders/builder/sections/OrderProcessingSection";
import { PricingSection } from "areas/orders/builder/sections/PricingSection";
import { useOrderDraft } from "areas/orders/builder/useOrderDraft";
import { useQuotePricePreview } from "areas/orders/builder/useQuotePricePreview";
import { fulfilmentReadFailed, fulfilmentReadPending } from "areas/orders/components/FulfilmentPanel";
import { LicenceGlyph } from "areas/orders/components/chrome/LicenceGlyph";
import { OrderStatusChip } from "areas/orders/components/chrome/OrderStatusChip";
import { ReviewChip } from "areas/orders/components/chrome/ReviewChip";
import { orderToasts } from "areas/orders/components/chrome/orderToasts";
import { OrderAcceptanceFacts } from "areas/orders/detail/OrderRecordCard";
import { useGuardedNavigate, useNavigationGuard } from "areas/orders/hooks/useUnsavedChangesGuard";
import { orderRoutes } from "areas/orders/order-routes";
import { getOrder, getOrderFulfillment } from "areas/orders/services/orderService";
import { describeOrderError, isOrderNotFound } from "areas/orders/utils/orderErrors";
import { maxPricedQuantity, validateOverrideReason, validatePricedQuantity } from "areas/orders/utils/orderQuantities";

function getValueOrFallbackFor(odNumber?: string) {
  return odNumber || "UNKNOWN_ORDER";
}

export const overrideReasonRequiredMessage = "Please provide a reason for each override.";
export const quantityInvalidMessage = `Each quantity must be a whole number, from 0 to ${maxPricedQuantity.toLocaleString(
  "en-US"
)}.`;
export const termStartRequiredMessage = "The term start date is required.";

function BuilderBreadcrumbs({ odNumber }: { odNumber: string }) {
  const guardedNavigate = useGuardedNavigate();

  return (
    <Breadcrumbs aria-label="Breadcrumb" sx={{ pb: 2, typography: "body2" }}>
      <Link component={RouterLink} to={orderRoutes.root} onClick={guardedNavigate(orderRoutes.root)}>
        Orders
      </Link>
      <Typography variant="body2" color="text.secondary">
        {odNumber}
      </Typography>
    </Breadcrumbs>
  );
}

function headerBadges(order: OrderDto) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <OrderStatusChip status={order.status} />
      <Chip label={`Motion: ${order.salesMotion}`} size="small" variant="outlined" />
      {order.reviewRequired && <ReviewChip reasons={order.reviewReasons} />}
    </Stack>
  );
}

function OrderNotFound({ odNumber }: { odNumber: string }) {
  return (
    <PageContent maxWidth={pageContentMaxWidth}>
      <BuilderBreadcrumbs odNumber={odNumber} />
      <Alert severity="warning">
        <AlertTitle>{`No order found for ${odNumber}`}</AlertTitle>
        <Typography variant="body2" component="div">
          Check the OD number, or pick an order from the list.
        </Typography>
        <Link component={RouterLink} to={orderRoutes.root} sx={{ mt: 1, display: "inline-block" }}>
          Back to Orders
        </Link>
      </Alert>
    </PageContent>
  );
}

function readStateOf(isLoading: boolean, error?: Error): OrderFulfillmentDto | undefined {
  if (isLoading) return fulfilmentReadPending;
  return error ? fulfilmentReadFailed(describeOrderError(error)) : undefined;
}

function OrderBuilder({
  order,
  canWrite,
  onOrderChanged,
}: {
  order: OrderDto;
  canWrite: boolean;
  onOrderChanged: (order: OrderDto) => void;
}) {
  const navigate = useNavigate();
  const { sendNewAlert } = useAlerts();
  const { draft, setField, isDirty, save, reset, applyServerOrder, applyServerOrderKeepingEdits } =
    useOrderDraft(order);
  const pricePreview = useQuotePricePreview(order, draft.quantities);
  const navigation = useNavigationGuard(isDirty);
  const [showTermProblems, setShowTermProblems] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | undefined>(undefined);
  const [isLiveSavePrompted, setIsLiveSavePrompted] = React.useState(false);

  const locked = isOrderClosedToEdits(order);
  const railReadOnly = !canWrite || order.isArchived;

  const loadFulfillment = React.useCallback(() => getOrderFulfillment(order.odNumber), [order.odNumber]);
  const {
    data: fulfillment,
    loading: isFulfillmentLoading,
    error: fulfillmentError,
    refresh: refreshFulfillment,
  } = useAsyncRequest(loadFulfillment);
  const fulfillmentView = fulfillment ?? readStateOf(isFulfillmentLoading, fulfillmentError);

  const readRecordAndOrder = () => {
    refreshFulfillment();
    void getOrder(order.odNumber).then(onOrderChanged, () => undefined);
  };

  const saveAndAnnounce = async () => {
    const specialLicenceTerms = validateSpecialLicenceTerms(draft.specialLicenceTerms);
    if (specialLicenceTerms !== true) {
      setShowTermProblems(true);
      throw new Error(specialLicenceTerms);
    }
    setShowTermProblems(false);

    const saved = await save();
    sendNewAlert(orderToasts.saved, AlertSeverity.Success);
    if (saved) {
      onOrderChanged(saved);
    }
  };

  const commitSave = async () => {
    setSaving(true);
    setSaveError(undefined);
    try {
      await saveAndAnnounce();
      setIsLiveSavePrompted(false);
    } catch (error) {
      setSaveError(describeOrderError(error));
    } finally {
      setSaving(false);
    }
  };

  const requestSave = async () => {
    setSaveError(undefined);
    if (draft.termStartDate.trim() === "") {
      setSaveError(termStartRequiredMessage);
      return;
    }
    if (draft.quantities.some((row) => validatePricedQuantity(row.pricedValue) !== true)) {
      setSaveError(quantityInvalidMessage);
      return;
    }
    // Why: the domain refuses a priced quantity that diverges from what was measured without a
    // reason, so the rows are checked before the call rather than after it.
    if (draft.quantities.some((row) => validateOverrideReason(row) !== true)) {
      setSaveError(overrideReasonRequiredMessage);
      return;
    }
    if (order.status === OrderStatus.Quote) {
      setIsLiveSavePrompted(true);
      return;
    }
    await commitSave();
  };

  return (
    <>
      <PageHeaderBar maxWidth={pageContentMaxWidth}>
        <BuilderBreadcrumbs odNumber={order.odNumber} />
        <DetailsPageLayout
          pageName={order.odNumber}
          pageNameSx={{ fontWeight: 500 }}
          titleIcon={<LicenceGlyph odNumber={order.odNumber} />}
          titleBadge={headerBadges(order)}
          actions={[
            <Button
              key="document"
              variant="outlined"
              onClick={() => navigation.guard(() => navigate(orderRoutes.detail(order.odNumber).document))}
            >
              {order.status === OrderStatus.Accepted || order.status === OrderStatus.Cancelled
                ? "Order document"
                : "Quote document"}
            </Button>,
            <Button
              key="history"
              variant="outlined"
              onClick={() => navigation.guard(() => navigate(orderRoutes.detail(order.odNumber).history))}
            >
              Audit history
            </Button>,
          ]}
        />
      </PageHeaderBar>

      <PageContent maxWidth={pageContentMaxWidth}>
        <BuilderLayout
          rail={
            <OrderSummaryRail
              order={order}
              draft={draft}
              price={pricePreview.price}
              isDirty={isDirty}
              readOnly={railReadOnly}
              onOrderChanged={onOrderChanged}
              fulfillment={fulfillmentView}
              onFulfillmentChanged={readRecordAndOrder}
            />
          }
        >
          {locked && <LockedOrderBanner order={order} onOrderChanged={onOrderChanged} />}

          {/* Why: Swashbuckle drops `nullable` from a $ref, so codegen types acceptance as non-null, but the API sends null before acceptance. */}
          {order.acceptance && (
            <BuilderSection title="Acceptance">
              <OrderAcceptanceFacts acceptance={order.acceptance} />
            </BuilderSection>
          )}

          <BillingAccountsSection order={order} draft={draft} setField={setField} readOnly={!canWrite || locked} />
          <CommercialTermsSection
            order={order}
            draft={draft}
            setField={setField}
            readOnly={!canWrite || locked}
            showTermProblems={showTermProblems}
          />
          <PricingSection
            order={order}
            draft={draft}
            setField={setField}
            readOnly={!canWrite || locked}
            isDirty={isDirty}
            pricePreview={pricePreview}
            onOrderChanged={(updated) => {
              applyServerOrder(updated);
              onOrderChanged(updated);
            }}
          />
          <OrderProcessingSection order={order} draft={draft} setField={setField} readOnly={!canWrite || locked} />
          <NotesSection order={order} draft={draft} setField={setField} readOnly={!canWrite || locked} />
          <InternalReviewSection
            order={order}
            draft={draft}
            setField={setField}
            readOnly={!canWrite || locked}
            fulfillment={fulfillment}
            onOrderChanged={applyServerOrderKeepingEdits}
          />

          {canWrite && !locked && (
            <Stack
              data-testid="builder-save-footer"
              spacing={1}
              sx={{
                alignItems: "flex-end",
                position: "sticky",
                bottom: 0,
                zIndex: (theme) => theme.zIndex.appBar - 1,
                bgcolor: "background.paper",
                boxShadow: 1,
                borderRadius: 1,
                borderTop: 1,
                borderColor: "divider",
                p: 2,
              }}
            >
              {!!saveError && !isLiveSavePrompted && (
                <Alert severity="error" sx={{ width: "100%" }}>
                  {saveError}
                </Alert>
              )}
              <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                {isDirty && <Chip label="Unsaved changes" color="warning" variant="outlined" size="small" />}
                <Button variant="contained" loading={saving} onClick={requestSave}>
                  Save changes
                </Button>
              </Stack>
            </Stack>
          )}
        </BuilderLayout>
      </PageContent>

      <UnsavedChangesDialog
        open={navigation.isBlocked}
        onKeepEditing={navigation.keepEditing}
        onDiscard={() => {
          reset();
          navigation.discard();
        }}
      />

      <ActionDialog
        title="Save changes to a live quote"
        description={
          <>
            This quote is published, and the customer is looking at the same record. Saving will change what they see.
            Continue?
            {!!saveError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {saveError}
              </Alert>
            )}
          </>
        }
        show={isLiveSavePrompted}
        confirmText="Save changes"
        confirmColor="primary"
        onConfirm={commitSave}
        onCancel={() => setIsLiveSavePrompted(false)}
      />
    </>
  );
}

export function OrderBuilderPage() {
  const { odNumber } = useParams<{ odNumber: string }>();
  const resolvedOdNumber = getValueOrFallbackFor(odNumber);
  const { isAuthorized } = useRbacContext();
  const canWrite = isAuthorized(RbacPolicies.Orders.ReadWrite);

  const { data: order, loading, error } = useAsyncRequest(getOrder, resolvedOdNumber);
  const [latestOrder, setLatestOrder] = useState<OrderDto | undefined>(undefined);
  const current = latestOrder?.odNumber === resolvedOdNumber ? latestOrder : order;

  if (isOrderNotFound(error)) {
    return <OrderNotFound odNumber={resolvedOdNumber} />;
  }

  return (
    <LoadablePage loading={loading && !current} error={error}>
      {current && (
        <OrderBuilder key={current.version} order={current} canWrite={canWrite} onOrderChanged={setLatestOrder} />
      )}
    </LoadablePage>
  );
}
